import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Menu, X } from 'lucide-react';
import { navItems } from '../data/mock';
import Logo from './Logo';

function NavLink({ item, onClick, className }) {
  if (item.route) {
    return (
      <Link to={item.href} onClick={onClick} className={className}>
        {item.label}
        <span className="absolute -bottom-1 left-0 w-0 h-px bg-[#FF3D2E] group-hover:w-full transition-all duration-300" />
      </Link>
    );
  }
  return (
    <a href={item.href} onClick={onClick} className={className}>
      {item.label}
      <span className="absolute -bottom-1 left-0 w-0 h-px bg-[#FF3D2E] group-hover:w-full transition-all duration-300" />
    </a>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Lock background scroll while the mobile menu is open — always release
  // to empty string on close so we never inherit a stuck 'hidden' state.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (open) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [open]);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'backdrop-blur-md bg-black/60 border-b border-white/5' : 'bg-transparent'}`} style={{ paddingTop: 'env(safe-area-inset-top)' }}>
      <div className="px-5 md:px-10 lg:px-14 py-4 md:py-6 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 select-none" data-testid="nav-logo">
          <Logo size="md" />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-9">
          {navItems.map((item) => (
            <NavLink
              key={item.label}
              item={item}
              className="relative text-[12px] tracking-[0.25em] text-white/75 hover:text-white transition-colors group"
            />
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {/* CTA — desktop full label, mobile compact */}
          <a
            href="/#contact"
            className="hidden md:inline-flex items-center gap-2 px-5 py-3 border border-white/25 text-[11px] tracking-[0.25em] text-white hover:bg-white hover:text-black transition-colors"
            data-testid="nav-cta-start"
          >
            START A PROJECT
            <ArrowUpRight className="w-3.5 h-3.5" />
          </a>
          {/* Compact mobile CTA — keeps a conversion path in view even before scroll */}
          <a
            href="/#contact"
            className="md:hidden inline-flex items-center gap-1.5 px-3.5 py-2 bg-[#FF3D2E] text-white text-[10px] tracking-[0.2em] font-medium hover:bg-[#ff5446] transition-colors"
            data-testid="nav-cta-start-mobile"
          >
            START
            <ArrowUpRight className="w-3 h-3" />
          </a>

          {/* Mobile menu trigger */}
          <button
            className="lg:hidden text-white w-10 h-10 -mr-2 flex items-center justify-center"
            onClick={() => setOpen(!open)}
            aria-label={open ? 'Close menu' : 'Open menu'}
            data-testid="nav-mobile-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu — full-screen, cinematic, staggered reveal */}
      <div
        className={`lg:hidden fixed inset-0 top-0 bg-black/98 backdrop-blur-xl transition-all duration-500 ease-out ${
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        style={{ paddingTop: 'calc(env(safe-area-inset-top) + 72px)' }}
        data-testid="nav-mobile-panel"
      >
        <div className="px-6 pt-8 pb-10 flex flex-col h-full">
          <nav className="flex flex-col gap-1">
            {navItems.map((item, i) => (
              <div
                key={item.label}
                style={{
                  transform: open ? 'translateY(0)' : 'translateY(12px)',
                  opacity: open ? 1 : 0,
                  transition: `opacity 400ms ease-out ${80 + i * 40}ms, transform 400ms ease-out ${80 + i * 40}ms`,
                }}
                className="border-b border-white/8"
              >
                <NavLink
                  item={item}
                  onClick={() => setOpen(false)}
                  className="relative block py-5 text-2xl font-display tracking-tight text-white/90 hover:text-[#FF3D2E] group"
                />
              </div>
            ))}
          </nav>

          <div
            className="mt-auto pt-8"
            style={{
              opacity: open ? 1 : 0,
              transform: open ? 'translateY(0)' : 'translateY(12px)',
              transition: `opacity 400ms ease-out ${80 + navItems.length * 40}ms, transform 400ms ease-out ${80 + navItems.length * 40}ms`,
            }}
          >
            <a
              href="/#contact"
              onClick={() => setOpen(false)}
              className="inline-flex items-center justify-center gap-2 w-full px-6 py-4 bg-[#FF3D2E] text-white text-[11px] tracking-[0.3em] hover:bg-[#ff5446] transition-colors"
              data-testid="nav-mobile-cta"
            >
              START A PROJECT <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
            <p className="mt-6 text-[10px] tracking-[0.3em] text-white/40 text-center">
              CARLSBAD · NASHVILLE · SAN DIEGO
            </p>
          </div>
        </div>
      </div>
    </header>
  );
}
